import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fetchProducts } from "../api";
import { categories } from "../mock";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NUMERALS = ["I", "II", "III"];

const withSrcSet = (src) => {
  if (!src?.startsWith(BACKEND)) return {};
  const small = src.replace(".webp", "-480.webp");
  return { srcSet: `${small} 480w, ${src} 1050w`, sizes: "(min-width: 1024px) 55vw, 100vw" };
};

const Tile = ({ cat, count, index, large }) => (
  <Link
    to={`/shop/${cat.id}`}
    data-testid={`collection-tile-${cat.id}`}
    className={`group relative block overflow-hidden bg-[var(--sand)] vc-img-zoom ${
      large ? "aspect-[4/5] lg:aspect-auto lg:h-full" : "aspect-[4/3] lg:aspect-auto lg:flex-1"
    }`}
  >
    <img
      src={cat.image}
      {...withSrcSet(cat.image)}
      alt={`${cat.name} collection`}
      loading="lazy"
      decoding="async"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div
      className="absolute inset-0 bg-gradient-to-t from-[var(--espresso-deep)]/85 via-[var(--espresso-deep)]/20 to-transparent transition-opacity duration-500 group-hover:opacity-95"
      aria-hidden="true"
    />
    <span className="absolute top-5 left-6 font-serif-display italic text-[var(--cream)]/70 text-2xl leading-none">
      {NUMERALS[index] || index + 1}
    </span>
    <span className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center border border-[var(--cream)]/30 text-[var(--cream)] opacity-0 -translate-y-1 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
      <ArrowUpRight className="w-4 h-4" />
    </span>
    <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
      <h3
        className={`font-serif-display font-light text-[var(--cream)] leading-[1.1] ${
          large ? "text-4xl lg:text-5xl" : "text-2xl lg:text-3xl"
        }`}
      >
        {cat.name}
      </h3>
      <div className="mt-3 flex items-center gap-4">
        <span className="w-8 h-px bg-[var(--copper-light)]" aria-hidden="true" />
        <p className="text-[10px] tracking-[0.3em] uppercase text-[var(--cream)]/70">
          {count} {count === 1 ? "piece" : "pieces"}
        </p>
      </div>
    </div>
  </Link>
);

const SignatureCollections = () => {
  const [live, setLive] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchProducts()
      .then((data) => {
        if (cancelled) return;
        const counts = data.reduce((acc, p) => {
          acc[p.category] = (acc[p.category] || 0) + 1;
          return acc;
        }, {});
        setLive(categories.filter((c) => counts[c.id] > 0).map((c) => ({ ...c, count: counts[c.id] })));
      })
      .catch(() => { if (!cancelled) setLive([]); });
    return () => { cancelled = true; };
  }, []);

  const tiles = useMemo(() => live.slice(0, 3), [live]);
  if (tiles.length === 0) return null;

  return (
    <section className="relative overflow-hidden bg-[var(--ivory)] py-28 lg:py-40 vc-grain" data-testid="signature-collections">
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-20 z-[2]">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 lg:mb-20 gap-8">
          <div className="vc-fade-up">
            <p className="vc-eyebrow text-[var(--copper)] mb-6" data-testid="collections-eyebrow">
              Signature Collections
            </p>
            <h2 className="font-serif-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.08] text-[var(--espresso)] max-w-xl">
              Explore by <em className="italic text-[var(--copper)]">collection</em>.
            </h2>
          </div>
          <p className="text-[var(--espresso)]/60 leading-[1.9] max-w-sm vc-fade-up vc-stagger-1 md:text-right">
            Every collection is its own study — choose the room you want to change first.
          </p>
        </div>

        {tiles.length === 1 ? (
          <div className="lg:h-[560px] vc-fade-up vc-stagger-1">
            <Tile cat={tiles[0]} count={tiles[0].count} index={0} large />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 lg:h-[720px]">
            <div className="lg:col-span-7 vc-fade-up vc-stagger-1 lg:h-full">
              <Tile cat={tiles[0]} count={tiles[0].count} index={0} large />
            </div>
            <div className="lg:col-span-5 flex flex-col gap-6 lg:gap-8 lg:h-full">
              {tiles.slice(1).map((cat, i) => (
                <div key={cat.id} className={`vc-fade-up vc-stagger-${i + 2} lg:flex-1 lg:min-h-0 lg:flex`}>
                  <Tile cat={cat} count={cat.count} index={i + 1} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SignatureCollections;
